import { TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelXS } from '@sorare/core/src/atoms/typography';
import { fantasy } from '@sorare/core/src/lib/glossary';
import { isType } from '@sorare/core/src/lib/gql';
import { teamPictureUrl } from '@sorare/core/src/lib/so5';

import type { MultipleMatchupItem_anyGame } from './__generated__/index.graphql';

const Item = styled(Horizontal).attrs({ gap: 0.5, center: true })`
  width: 100%;
  height: 100%;
  color: var(--c-nd-600);

  &.highlighted {
    background-color: var(--c-nd-50);
    color: var(--c-white);
  }

  &:not(:last-child) {
    border-right: 1px solid var(--c-black);
  }
`;
const Img = styled.img`
  object-fit: contain;
`;

type Props = {
  playerTeamSlug?: string;
  anyGame: MultipleMatchupItem_anyGame;
};

export const MultipleMatchupItem = ({ playerTeamSlug, anyGame }: Props) => {
  const { formatMessage } = useIntl();

  const isPlayingHome = playerTeamSlug === anyGame.homeTeam?.slug;
  const notPlayerTeam = isPlayingHome ? anyGame.awayTeam : anyGame.homeTeam;

  const notPlayerTeamPictureUrl = notPlayerTeam
    ? teamPictureUrl(notPlayerTeam)
    : null;

  return (
    <Item
      key={anyGame.id}
      className={classNames({ highlighted: isPlayingHome })}
      aria-label={formatMessage(fantasy.game)}
    >
      {isType(anyGame, 'Game') ? (
        <>
          {isPlayingHome ? (
            <svg
              aria-label={formatMessage(fantasy.home)}
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="10"
              viewBox="0 0 16 15"
              fill="none"
            >
              <path
                d="M8 0L0 6V8H1V15H4V10H7V15H15V8H16V6L14 4.5V1H11V2.25L8 0ZM9 10H12V13H9V10Z"
                fill="currentColor"
              />
            </svg>
          ) : (
            <svg
              aria-label={formatMessage(fantasy.away)}
              xmlns="http://www.w3.org/2000/svg"
              width="10"
              height="10"
              viewBox="0 0 16 16"
              fill="none"
            >
              <path
                d="M16 7L14 6L11 6L7 -3.93402e-07L5 -4.80825e-07L5 1L6 6L3 7L2 5L-2.18557e-07 5L-4.80825e-07 11L2 11L3 9L6 10L5 15L5 16L7 16L11 10L14 10L16 9L16 7Z"
                fill="currentColor"
              />
            </svg>
          )}
          <LabelXS>{notPlayerTeam?.code}</LabelXS>
          {notPlayerTeamPictureUrl && (
            <Img
              src={notPlayerTeamPictureUrl}
              alt={notPlayerTeam?.slug}
              width={12}
              height={12}
            />
          )}
        </>
      ) : (
        <>
          <LabelXS>
            {isPlayingHome ? (
              <FormattedMessage id="Matchups.vsTeamBrief" defaultMessage="vs" />
            ) : (
              <FormattedMessage id="Matchups.atTeamBrief" defaultMessage="@" />
            )}
          </LabelXS>
          {notPlayerTeamPictureUrl && (
            <Img
              src={notPlayerTeamPictureUrl}
              alt={notPlayerTeam?.slug}
              width={12}
              height={12}
            />
          )}
        </>
      )}
    </Item>
  );
};

MultipleMatchupItem.fragments = {
  anyGame: gql`
    fragment MultipleMatchupItem_anyGame on AnyGameInterface {
      id
      homeTeam {
        slug
        code
        ...teamPictureUrl_teamInterface
      }
      awayTeam {
        slug
        code
        ...teamPictureUrl_teamInterface
      }
    }
    ${teamPictureUrl.fragments.teamInterface}
  ` as TypedDocumentNode<MultipleMatchupItem_anyGame>,
};
